import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, RefreshCw, Save } from "lucide-react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosinstance";
import { localCalendarDate } from "../utils/localDate";
import "./TBTDailySummary.css";

const selectedProjectId = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("selectedProject") || "null");
    return String(stored?.project_id || stored?.id || stored || "").trim();
  } catch { return ""; }
};

const apiError = (error, fallback) => error?.response?.data?.message || fallback;

const normalizeCategories = (categories) => (Array.isArray(categories) ? categories : [])
  .map((category) => ({
    key: category.category_key,
    name: category.category_name,
    headcount: Number.isInteger(category.headcount) ? category.headcount : 0,
    remarks: category.remarks || "",
  }));

export default function ManpowerDailyReport({ isSidebarExpanded }) {
  const [projectId, setProjectId] = useState(selectedProjectId);
  const [summaryDate, setSummaryDate] = useState(localCalendarDate);
  const [categories, setCategories] = useState([]);
  const [originalCategories, setOriginalCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [expandedRemarks, setExpandedRemarks] = useState(null);

  useEffect(() => {
    const syncProject = () => {
      setProjectId(selectedProjectId());
      setSummaryDate(localCalendarDate());
      setCategories([]);
      setOriginalCategories([]);
      setExpandedRemarks(null);
      setError("");
    };
    window.addEventListener("projectChanged", syncProject);
    window.addEventListener("storage", syncProject);
    return () => { window.removeEventListener("projectChanged", syncProject); window.removeEventListener("storage", syncProject); };
  }, []);

  const loadReport = useCallback(async (signal) => {
    if (!projectId) {
      setCategories([]); setOriginalCategories([]); setError(""); setLoading(false);
      return;
    }
    setLoading(true); setError("");
    try {
      const response = await axiosInstance.get(`/api/projects/${projectId}/manpower-daily`, { params: { date: summaryDate }, signal });
      const rows = normalizeCategories(response?.data?.categories);
      setCategories(rows); setOriginalCategories(rows); setExpandedRemarks(null);
    } catch (requestError) {
      if (requestError?.code !== "ERR_CANCELED") {
        setCategories([]);
        setError(apiError(requestError, "Unable to load the manpower daily report."));
      }
    } finally { if (!signal?.aborted) setLoading(false); }
  }, [projectId, summaryDate]);

  useEffect(() => {
    const controller = new AbortController();
    loadReport(controller.signal);
    return () => controller.abort();
  }, [loadReport]);

  const totalManpower = useMemo(() => categories.reduce((sum, row) => sum + (Number(row.headcount) || 0), 0), [categories]);
  const modifiedKeys = useMemo(() => {
    const originals = new Map(originalCategories.map((row) => [row.key, row]));
    return new Set(categories.filter((row) => {
      const original = originals.get(row.key);
      return !original || Number(row.headcount) !== Number(original.headcount) || row.remarks.trim() !== (original.remarks || "").trim();
    }).map((row) => row.key));
  }, [categories, originalCategories]);
  const hasUnsavedChanges = modifiedKeys.size > 0;

  const updateCategory = (key, field, value) => setCategories((current) => current.map((row) => (row.key === key ? { ...row, [field]: value } : row)));

  const saveReport = async () => {
    if (!projectId || loading || saving || !hasUnsavedChanges) return;
    const payloadCategories = categories.map((row) => ({ category_key: row.key, headcount: Number(row.headcount), remarks: row.remarks.trim() || null }));
    if (payloadCategories.some((row) => !Number.isInteger(row.headcount) || row.headcount < 0)) {
      setError("Each headcount must be a whole number of zero or more.");
      return;
    }
    setSaving(true); setError("");
    try {
      const response = await axiosInstance.put(`/api/projects/${projectId}/manpower-daily/${summaryDate}`, { categories: payloadCategories });
      const saved = normalizeCategories(response?.data?.categories);
      setCategories(saved); setOriginalCategories(saved);
      toast.success("Manpower daily report saved successfully.");
      window.dispatchEvent(new Event("dashboardMetricsStale"));
    } catch (requestError) {
      setError(apiError(requestError, "Unable to save the manpower daily report."));
    } finally { setSaving(false); }
  };

  return <main className={`table-container tbt-daily-page ${isSidebarExpanded ? "expanded" : "collapsed"}`}>
    <div className="tbt-daily-content">
      <header className="tbt-daily-header">
        <div className="tbt-heading"><h1>Manpower Daily Report</h1><p>Record project-wise deployed manpower by work category.</p></div>
        <div className="tbt-daily-actions" aria-label="Manpower report controls">
          <label className="tbt-date-field"><span>Date</span><input type="date" value={summaryDate} onChange={(event) => setSummaryDate(event.target.value)} aria-label="Report date" disabled={!projectId} /></label>
          <button type="button" className="tbt-icon-button" onClick={() => loadReport()} disabled={!projectId || loading || saving} aria-label="Refresh manpower report"><RefreshCw size={17} className={loading ? "tbt-spin" : ""} aria-hidden="true" /></button>
          <button type="button" className="tbt-save-button" onClick={saveReport} disabled={!projectId || loading || saving || !hasUnsavedChanges}><Save size={16} aria-hidden="true" /> {saving ? "Saving…" : "Save Report"}</button>
        </div>
      </header>
      {!projectId && <section className="tbt-empty-state"><strong>Select a project</strong><p>Select a project from the sidebar before recording its manpower report.</p></section>}
      {projectId && error && <div className="tbt-message tbt-error" role="alert">{error}</div>}
      {projectId && <section className="tbt-summary-card" aria-busy={loading}>
        {loading ? <div className="tbt-loading">Loading manpower report…</div> : <div className="tbt-table-scroll"><table className="tbt-topic-table"><thead><tr><th scope="col">#</th><th scope="col">Work Category</th><th scope="col">Headcount</th><th scope="col">Remarks</th></tr></thead><tbody>
          {categories.map((category, index) => {
            const expanded = expandedRemarks === category.key;
            return <tr key={category.key} className={`${expanded ? "is-expanded " : ""}${modifiedKeys.has(category.key) ? "is-modified" : ""}`}><td><span className="tbt-topic-number">{index + 1}</span></td><td><strong>{category.name}</strong></td><td><input className="tbt-count-input" type="number" min="0" step="1" inputMode="numeric" aria-label={`${category.name} headcount`} value={category.headcount} onChange={(event) => updateCategory(category.key, "headcount", event.target.value)} /></td><td><div className="tbt-remarks-control"><textarea className={expanded ? "is-expanded" : ""} rows={expanded ? 3 : 1} maxLength="1000" value={category.remarks} onFocus={() => setExpandedRemarks(category.key)} onChange={(event) => updateCategory(category.key, "remarks", event.target.value)} placeholder="Optional remark" /><button type="button" onClick={() => setExpandedRemarks(expanded ? null : category.key)} aria-label={`${expanded ? "Collapse" : "Expand"} remarks for ${category.name}`}>{expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</button></div></td></tr>;
          })}
        </tbody><tfoot><tr><td colSpan="2">Total Manpower</td><td>{totalManpower}</td><td>{hasUnsavedChanges ? `${modifiedKeys.size} modified` : "No unsaved changes"}</td></tr></tfoot></table></div>}
      </section>}
    </div>
  </main>;
}
